import { useEffect, useState } from "react";
import { Loader2, Lock, Scale, FileSearch } from "lucide-react";

/**
 * AnalysisOverlay — Overlay plein écran pendant l'appel API (loading uniquement).
 * Chronomètre en VRAIES SECONDES (setInterval 1000 ms).
 * Aucune étape "succès / Retour" : le parent démonte l'overlay dès réception du JSON,
 * la redirection vers le Dossier Flash 4-blocs est immédiate.
 */
const PHASES = [
  { min: 0, max: 3,  icon: <Lock className="w-4 h-4" />,       label: "Lecture et anonymisation RGPD de votre situation…" },
  { min: 4, max: 8,  icon: <Scale className="w-4 h-4" />,      label: "Interrogation des bases légales (Loi du 6 juillet 1989, Légifrance)…" },
  { min: 9, max: 99, icon: <FileSearch className="w-4 h-4" />, label: "Structuration des faits, calcul du score et synthèse en cours…" },
];

function currentPhase(sec) {
  return PHASES.find((p) => sec >= p.min && sec <= p.max) || PHASES[PHASES.length - 1];
}

export function AnalysisOverlay() {
  const [sec, setSec] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setSec((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const capped = Math.min(sec, 47);
  const mm = "00";
  const ss = String(capped).padStart(2, "0");
  const pct = Math.min(100, (capped / 47) * 100);
  const phase = currentPhase(sec);

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/95 backdrop-blur-sm flex items-center justify-center px-4" data-testid="analysis-overlay" role="dialog" aria-modal="true" aria-label="Analyse en cours">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6">
          <Loader2 className="w-6 h-6 text-emerald-600 animate-spin flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest font-black text-emerald-700">
              Recherche et analyse en cours…
            </div>
            <div className="font-heading font-black text-2xl text-slate-900 tabular-nums" data-testid="analysis-chrono">
              {mm}:{ss}<span className="text-slate-400 text-lg"> / 00:47</span>
            </div>
          </div>
        </div>

        <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-6">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all duration-1000"
            style={{ width: `${pct}%` }}
            role="progressbar"
            aria-valuenow={Math.round(pct)}
            aria-valuemin={0}
            aria-valuemax={100}
            data-testid="analysis-progress"
          />
        </div>

        <div className="flex items-start gap-3 p-3 rounded-xl bg-emerald-50 border border-emerald-100" data-testid="analysis-phase">
          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-emerald-600 text-white">
            {phase.icon}
          </div>
          <div className="text-sm text-slate-800 leading-snug pt-1">{phase.label}</div>
        </div>

        <div className="mt-6 text-center text-xs text-slate-500">
          Loi 71-1130 · Extraction factuelle · Sources officielles Etalab 2.0
        </div>
      </div>
    </div>
  );
}
